import React, { useMemo } from "react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/atoms/Avatar/Avatar";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import CheckCircleFilled from "@/icons/__generated__/CheckCircleFilled/CheckCircleFilled16";
import StarFilled16 from "@/icons/__generated__/StarFilled/StarFilled16";
import XCircleFilled from "@/icons/__generated__/XCircleFilled/XCircleFilled16";

interface ReviewProps {
  avatar: string;
  reviewerId?: string;
  reviewText?: string;
  formattedDate?: string;
  stars?: number;
  bookAgain?: boolean;
  firstName?: string;
  lastName?: string;
  activityStatement?: React.ReactNode;
  bookAgainText?: React.ReactNode;
  notBookAgainText?: React.ReactNode;
  yesText?: React.ReactNode;
  noText?: React.ReactNode;
  profession?: string;
  isAttendeeReview?: boolean;
  productSchemaId?: string;
}

export const Review: React.FC<ReviewProps> = ({
  avatar,
  reviewText = "",
  formattedDate = "",
  stars = 5,
  bookAgain = true,
  firstName = "",
  lastName = "",
  activityStatement = "",
  bookAgainText = "",
  notBookAgainText = "",
  yesText = "",
  noText = "",
  reviewerId = "",
  profession = "",
  isAttendeeReview = false,
  productSchemaId = "",
}) => {
  const filledStars = Array.from({ length: stars }, (_, i) => (
    <StarFilled16 data-testid="star-filled" key={`filled-star-${i}`} />
  ));
  const emptyStars = Array.from({ length: 5 - stars }, (_, i) => (
    <StarFilled16
      data-testid="star-empty"
      className="tw-text-gray-400"
      key={`empty-star-${i}`}
    />
  ));
  const ratingStars = [...filledStars, ...emptyStars];

  const yesOrNo = bookAgain ? yesText : noText;
  const bookAgainStatement = bookAgain ? bookAgainText : notBookAgainText;
  const statementIcon = bookAgain ? (
    <CheckCircleFilled className="tw-text-green-800" />
  ) : (
    <XCircleFilled className="tw-text-gray-500" />
  );

  const reviewJsonLd = useMemo(() => {
    return productSchemaId
      ? {
          "@context": "https://schema.org",
          "@type": "Review",
          itemReviewed: {
            "@type": "Product",
            "@id": productSchemaId,
          },
          author: {
            "@type": "Person",
            name: `${firstName} ${lastName}`.trim(),
          },
          reviewRating: {
            "@type": "Rating",
            ratingValue: stars.toString(),
            bestRating: "5",
          },
          reviewBody: reviewText,
          ...(formattedDate ? { datePublished: formattedDate } : {}),
        }
      : null;
  }, [firstName, lastName, stars, reviewText, formattedDate, productSchemaId]);

  return (
    <article>
      {reviewJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewJsonLd) }}
        />
      )}
      <header className="tw-flex tw-gap-12 tw-items-center tw-mb-24">
        <div className="tw-w-48 tw-h-48">
          <Avatar size={48}>
            <AvatarImage
              src={avatar}
              alt={`Profile image of ${firstName} ${lastName}`}
            />
            <AvatarFallback
              className="tw-h-48"
              alt="User has no profile image"
            />
          </Avatar>
        </div>
        <div>
          <div>
            <Button
              variant="text-only"
              className="tw-body-2-m"
              href={`/profiles/${reviewerId}`}
            >
              {firstName} {lastName}
            </Button>
            {!isAttendeeReview && !!profession && (
              <Text className="tw-ml-6">
                {"("}
                {profession}
                {")"}
              </Text>
            )}
          </div>
          <div className="tw-body-1 tw-text-gray-600 first-letter:tw-uppercase tw-lowercase">
            {activityStatement}
          </div>
        </div>
      </header>
      <div>
        <div className="tw-flex tw-gap-12 tw-mb-8">
          <div className="tw-flex">{ratingStars}</div>
          <div className="tw-flex tw-gap-4">
            <Text
              typeface="body-1-m"
              prefix={statementIcon}
              className="tw-gap-4"
            >
              {yesOrNo}
            </Text>
            <Text typeface="body-1">{bookAgainStatement}</Text>
          </div>
        </div>
        <div>
          <p className="tw-body-2 tw-mb-8">{reviewText}</p>
        </div>
        <div>
          <p className="tw-body-1-long tw-text-gray-600">{formattedDate}</p>
        </div>
      </div>
    </article>
  );
};

interface ReviewSummaryProps {
  avatar: string;
  reviewText?: string;
  firstName?: string;
  lastName?: string;
  productSchemaId?: string;
}

export const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  avatar,
  reviewText = "",
  firstName = "",
  lastName = "",
  productSchemaId = "",
}) => {
  const reviewSummaryJsonLd = useMemo(() => {
    return productSchemaId
      ? {
          "@context": "https://schema.org",
          "@type": "Review",
          itemReviewed: {
            "@type": "Product",
            "@id": productSchemaId,
          },
          author: {
            "@type": "Person",
            name: `${firstName} ${lastName}`.trim(),
          },
          reviewBody: reviewText,
        }
      : null;
  }, [firstName, lastName, reviewText, productSchemaId]);

  return (
    <div className="tw-flex tw-items-center tw-gap-12">
      {reviewSummaryJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(reviewSummaryJsonLd),
          }}
        />
      )}
      <div className="tw-w-48 tw-h-48">
        <Avatar size={48}>
          <AvatarImage
            src={avatar}
            alt={`Profile image of ${firstName} ${lastName}`}
          />
          <AvatarFallback className="tw-h-48" alt="User has no profile image" />
        </Avatar>
      </div>
      <div>
        <p className="tw-body-2 tw-text-gray-900">{reviewText}</p>
        <p className="tw-body-1 tw-text-gray-600">
          {firstName} {lastName}
        </p>
      </div>
    </div>
  );
};
